/**
 * ActionDock — fixed bottom dock with the active action mode (.abar / .amode).
 *
 * Faithful port of all 14 action modes from the canonical wireframe
 * (docs/wireframes/celestia/03-screen.html, `.amode` markup, lines ~432–498;
 * setP7/applyTurbo logic in the <script> block, lines ~558–609).
 *
 * Only one `.amode.on` is visible at a time. Mode selection:
 *   - system phases (P9/P10) → am-9 / am-10
 *   - not actor in interactive phase → am-wait
 *   - P1 actor → am-1
 *   - P2 actor → am-2
 *   - P2b actor → am-2b
 *   - P3 actor → am-3
 *   - P4 actor → am-4
 *   - P5 actor → am-5
 *   - P6 actor with reaction cards → am-6 / am-6n (no cards)
 *   - P7 actor (captain) → am-7
 *   - P8 actor → am-8
 *
 * Behavior (which actions to submit) comes from GameScreen.tsx / the engine.
 * Visuals (class names, layout, copy) come from the wireframe.
 */

import type { ViewerState } from "@cloudnest/celestia-engine";
import { useState } from "react";
import type { UseLocaleReturn } from "../i18n/useLocale";
import { PHASE_BADGE } from "../screen-constants";
import type { CelestiaActionHandlers } from "./types";

// ── helpers ────────────────────────────────────────────────────────────────

/** Symbol → equipment card icon id and color var. */
const SYMBOL_EQUIP: Readonly<
  Record<
    string,
    {
      readonly icon: string;
      readonly colorVar: string;
      readonly label: string;
      readonly cardLabel: string;
    }
  >
> = {
  fog: {
    icon: "ic-fog",
    colorVar: "var(--cn-celestia-event-fog)",
    label: "안개숲",
    cardLabel: "나침반",
  },
  pirate: {
    icon: "ic-pirate",
    colorVar: "var(--cn-celestia-event-pirate)",
    label: "해적",
    cardLabel: "대포",
  },
  lightning: {
    icon: "ic-lightning",
    colorVar: "var(--cn-celestia-event-lightning)",
    label: "번개",
    cardLabel: "터보",
  },
  birds: {
    icon: "ic-birds",
    colorVar: "var(--cn-celestia-event-birds)",
    label: "새 떼",
    cardLabel: "나팔",
  },
};

/** Derive whether the viewer's hand contains reaction cards for W6. */
function handHasReaction(
  hand: readonly string[] | null,
  kind: "jetpack" | "windGust" | "altRoute",
): boolean {
  const PREFIX: Readonly<Record<string, string>> = {
    jetpack: "pw-jetpack",
    windGust: "pw-windGust",
    altRoute: "pw-alternativeRoute",
  };
  return hand?.some((id) => id.startsWith(PREFIX[kind] ?? "")) ?? false;
}

/** Can the hand pay for the required symbols (forced non-turbo)? */
function hasEquipForSymbol(
  hand: readonly string[] | null,
  sym: string,
): boolean {
  const COLOUR: Record<string, string> = {
    fog: "blue",
    pirate: "black",
    birds: "red",
    lightning: "yellow",
  };
  const colour = COLOUR[sym];
  if (!colour) return false;
  return hand?.some((id) => id.startsWith(`eq-${colour}`)) ?? false;
}

/** Find first card id for a given symbol from hand. */
function equipCardIdForSymbol(
  hand: readonly string[] | null,
  sym: string,
): string | null {
  const COLOUR: Record<string, string> = {
    fog: "blue",
    pirate: "black",
    birds: "red",
    lightning: "yellow",
  };
  const colour = COLOUR[sym];
  if (!colour || !hand) return null;
  return hand.find((id) => id.startsWith(`eq-${colour}`)) ?? null;
}

// ── component ───────────────────────────────────────────────────────────────

interface Props {
  readonly viewerState: ViewerState;
  readonly myPlayerId: string | null;
  readonly actions: CelestiaActionHandlers;
  readonly i18n: UseLocaleReturn;
}

export function ActionDock({
  viewerState,
  myPlayerId,
  actions,
  i18n: _i18n,
}: Props) {
  const [turboOn, setTurboOn] = useState(true);
  const [ejectionTarget, setEjectionTarget] = useState<string | null>(null);

  const aircraft = viewerState.vessels.find((v) => v.kind === "aircraft");
  const captainPlayerId = aircraft?.captainPlayerId ?? "";
  const phaseId = viewerState.currentPhase.phase;
  const badge = PHASE_BADGE[phaseId] ?? "P?";
  const legalActors = viewerState.currentPhase.legalActorSet;
  const isActor = myPlayerId !== null && legalActors.includes(myPlayerId);
  const me = viewerState.players.find((p) => p.playerId === myPlayerId);
  const hand = me?.hand ?? null;
  const isCaptain = myPlayerId !== null && myPlayerId === captainPlayerId;
  const ctx = viewerState.currentPhase.context;

  // Derive the active mode id (mirrors wireframe STATES logic)
  type ModeId =
    | "am-1"
    | "am-2"
    | "am-2b"
    | "am-3"
    | "am-4"
    | "am-5"
    | "am-6"
    | "am-6n"
    | "am-7"
    | "am-8"
    | "am-9"
    | "am-10"
    | "am-wait";

  let modeId: ModeId = "am-wait";

  if (phaseId === "EndOfLeg") {
    modeId = "am-9";
  } else if (phaseId === "EndOfJourney") {
    modeId = "am-10";
  } else if (!isActor) {
    modeId = "am-wait";
  } else {
    switch (phaseId) {
      case "AwaitSoloCaptainChoice":
        modeId = "am-1";
        break;
      case "AwaitRoll":
        modeId = "am-2";
        break;
      case "AwaitCaptainClaim":
        modeId = "am-2b";
        break;
      case "AwaitPassengerDecision":
        modeId = "am-3";
        break;
      case "AwaitEjectionWindow":
        modeId = "am-4";
        break;
      case "AwaitCaptainDeclaration":
        modeId = "am-5";
        break;
      case "AwaitReactionWindow": {
        const hasAny =
          handHasReaction(hand, "jetpack") ||
          handHasReaction(hand, "windGust") ||
          handHasReaction(hand, "altRoute");
        modeId = hasAny ? "am-6" : "am-6n";
        break;
      }
      case "AwaitResolution":
        modeId = "am-7";
        break;
      case "AwaitTreasureChoice":
        modeId = "am-8";
        break;
      default:
        modeId = "am-wait";
    }
  }

  // Next actor info for am-wait
  const nextActorId = legalActors[0] ?? null;
  const nextActorPlayer = viewerState.players.find(
    (p) => p.playerId === nextActorId,
  );
  const nextActorSeatLabel =
    nextActorPlayer !== undefined
      ? `P${nextActorPlayer.seat + 1} ${nextActorPlayer.displayName}`
      : "—";

  // P7 pay matrix logic (mirrors wireframe setP7 / applyTurbo)
  const requiredSymbols: readonly string[] =
    ctx.phase === "AwaitResolution" ? (ctx.requiredSymbols ?? []) : [];

  // Classify each required symbol
  type SymMode = "locked" | "turbo" | "wreck";
  const symModes: { sym: string; mode: SymMode }[] = requiredSymbols.map(
    (sym) => {
      if (sym === "lightning") return { sym, mode: "turbo" };
      if (hasEquipForSymbol(hand, sym)) return { sym, mode: "locked" };
      return { sym, mode: "wreck" };
    },
  );
  const allForced = symModes.every((s) => s.mode === "locked");
  const hasTurboSym = symModes.some((s) => s.mode === "turbo");
  const hasWreck = symModes.some((s) => s.mode === "wreck");
  const hasSpyglass =
    hand?.some(
      (id) => id.includes("spyglass") || id.startsWith("pw-spyglass"),
    ) ?? false;

  // Auto-derive forced equipment card ids
  const forcedCardIds = symModes
    .filter((s) => s.mode === "locked")
    .map((s) => equipCardIdForSymbol(hand, s.sym))
    .filter((id): id is string => id !== null);
  // Add turbo if toggled
  const turboCardId = turboOn
    ? (hand?.find((id) => id.startsWith("turbo")) ?? null)
    : null;
  const payCardIds = turboCardId
    ? [...forcedCardIds, turboCardId]
    : forcedCardIds;

  // P7 message + button states
  let p7Msg = "";
  let p7MsgClass = "";
  let p7CanGo = false;
  let p7CanCrash = false;

  if (allForced && !hasTurboSym) {
    p7Msg =
      "일반 장비(나침반·대포)는 강제 사용 — 위기 통과 (선장 임의 난파 불가)";
    p7MsgClass = "ok";
    p7CanGo = true;
  } else if (hasTurboSym && !hasWreck) {
    if (turboOn) {
      p7Msg = "일반 장비는 강제 사용 · 번개는 터보로 극복 — 통과 가능";
      p7MsgClass = "ok";
      p7CanGo = true;
      p7CanCrash = false;
    } else {
      p7Msg = "번개 미대응 — 터보 미사용 → 비행선 난파(실패)";
      p7MsgClass = "bad";
      p7CanGo = false;
      p7CanCrash = true;
    }
  } else if (hasWreck) {
    p7Msg = "안개숲 대응(나침반)·터보 모두 없음 — 비행선 난파";
    p7MsgClass = "bad";
    p7CanGo = false;
    p7CanCrash = true;
  } else {
    p7Msg = "모든 위기 대응됨 — 통과 가능";
    p7MsgClass = "ok";
    p7CanGo = true;
  }

  // P4 ejection targets (passengers, excluding captain + disconnected)
  const _passengers = aircraft?.passengers ?? [];
  const ejectionTargets = viewerState.players.filter(
    (p) =>
      p.playerId !== captainPlayerId &&
      p.isOnAircraft &&
      p.connectionStatus !== "disconnected",
  );

  // Current city for P8
  const currentCity = aircraft?.currentCity ?? 1;
  const treasureCityId =
    ctx.phase === "AwaitTreasureChoice" ? ctx.cityId : currentCity;

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="abar" id="abar">
      {/* am-wait */}
      <div className={modeId === "am-wait" ? "amode on" : "amode"} id="am-wait">
        <div className="ah">
          <span className="pn">{badge}</span>
          <span className="ins">다른 플레이어의 행동 대기</span>
          <span className="tmr">대기</span>
        </div>
        <div className="ab">
          <div className="ainfo">
            <span className="sp" aria-hidden="true" />
            <span>{nextActorSeatLabel}의 선택을 기다리는 중입니다…</span>
          </div>
        </div>
      </div>

      {/* am-1 — solo captain */}
      <div className={modeId === "am-1" ? "amode on" : "amode"} id="am-1">
        <div className="ah">
          <span className="pn">P1</span>
          <span className="ins">
            단독 선장 — 비행선에 혼자 (착륙 또는 계속)
          </span>
          <span className="tmr">선장</span>
        </div>
        <div className="ab">
          <div className="acts">
            <button
              className="uact gray def"
              data-testid="celestia-confirm-landing-btn"
              type="button"
              onClick={actions.confirmLanding}
            >
              <svg className="ico" aria-hidden="true">
                <use href="#ic-spyglass" />
              </svg>
              현재 도시 착륙 (보물·여정 종료)
            </button>
            <button
              className="uact primary"
              data-testid="celestia-roll-btn"
              type="button"
              onClick={actions.roll}
            >
              계속 — 주사위 굴리기
            </button>
          </div>
        </div>
      </div>

      {/* am-2 — roll */}
      <div className={modeId === "am-2" ? "amode on" : "amode"} id="am-2">
        <div className="ah">
          <span className="pn">P2</span>
          <span className="ins">주사위 굴리기</span>
          <span className="tmr">선장</span>
        </div>
        <div className="ab">
          <div className="acts">
            <button
              className="uact primary def"
              data-testid="celestia-roll-btn"
              type="button"
              onClick={actions.roll}
            >
              주사위 굴리기
            </button>
          </div>
        </div>
      </div>

      {/* am-2b — captain claim */}
      <div className={modeId === "am-2b" ? "amode on" : "amode"} id="am-2b">
        <div className="ah">
          <span className="pn">P2b</span>
          <span className="ins">
            선장 공표 — 해결 가능/불가 (공개 · 거짓 가능)
          </span>
          <span className="tmr">선장</span>
        </div>
        <div className="ab">
          <div className="ainfo" style={{ marginBottom: 9 }}>
            <span>
              전 여행자에게 공개됨 · <b>손패와 무관하게</b> 선언 가능(거짓
              허용). 승객은 이 공표를 보고 잔류/이탈을 결정.
            </span>
          </div>
          <div className="acts">
            <button
              className="uact warn def"
              data-testid="celestia-declare-fail-btn"
              type="button"
              onClick={() => actions.declareIntent("fail")}
            >
              해결 불가
            </button>
            <button
              className="uact go"
              data-testid="celestia-declare-pass-btn"
              type="button"
              onClick={() => actions.declareIntent("pass")}
            >
              해결 가능
            </button>
          </div>
        </div>
      </div>

      {/* am-3 — passenger decision */}
      <div className={modeId === "am-3" ? "amode on" : "amode"} id="am-3">
        <div className="ah">
          <span className="pn">P3</span>
          <span className="ins">탑승 유지 / 이탈</span>
          <span className="tmr">내 차례</span>
        </div>
        <div className="ab">
          <div className="acts">
            <button
              className="uact gray def"
              data-testid="celestia-leave-btn"
              type="button"
              onClick={() => actions.decide("leave")}
            >
              이탈
            </button>
            <button
              className="uact gray"
              data-testid="celestia-stay-btn"
              type="button"
              onClick={() => actions.decide("stay")}
            >
              탑승 유지
            </button>
          </div>
        </div>
      </div>

      {/* am-4 — ejection window */}
      <div className={modeId === "am-4" ? "amode on" : "amode"} id="am-4">
        <div className="ah">
          <span className="pn">P4</span>
          <span className="ins">
            강제 하차 — 추방 카드 사용 (대상 선택, 선장 제외) 또는 (액션 없음)
          </span>
          <span className="tmr">내 차례</span>
        </div>
        <div className="ab">
          <div className="rxrow">
            {/* Captain — cannot be ejected */}
            {(() => {
              const captain = viewerState.players.find(
                (p) => p.playerId === captainPlayerId,
              );
              return (
                <div className="rxopt">
                  <div className="rxcard off">
                    <svg className="ico" aria-hidden="true">
                      <use href="#ic-caphat" />
                    </svg>
                    <span>P{(captain?.seat ?? 0) + 1} 선장</span>
                  </div>
                  <button
                    className="rxbtn"
                    disabled
                    type="button"
                    title="선장은 추방 효과의 대상이 될 수 없음"
                  >
                    대상 불가
                  </button>
                </div>
              );
            })()}
            {/* Ejectable passengers */}
            {ejectionTargets.map((p) => (
              <div className="rxopt" key={p.playerId}>
                <div className="rxcard">
                  <svg className="ico" aria-hidden="true">
                    <use href="#ic-grapple" />
                  </svg>
                  <span>
                    P{p.seat + 1} {p.displayName}
                  </span>
                </div>
                <button
                  className={
                    ejectionTarget === p.playerId ? "rxbtn sel" : "rxbtn"
                  }
                  type="button"
                  onClick={() => {
                    setEjectionTarget(p.playerId);
                    actions.eject(p.playerId);
                  }}
                >
                  하차
                </button>
              </div>
            ))}
            {/* Pass */}
            <button
              className={ejectionTarget === null ? "rxnone sel" : "rxnone"}
              data-testid="celestia-pass-btn"
              type="button"
              title="추방하지 않음 · timeout 시 자동 선택"
              onClick={() => {
                setEjectionTarget(null);
                actions.eject(null);
              }}
            >
              (액션
              <br />
              없음)
            </button>
          </div>
        </div>
      </div>

      {/* am-5 — captain declaration (hand-bound) */}
      <div className={modeId === "am-5" ? "amode on" : "amode"} id="am-5">
        <div className="ah">
          <span className="pn">P5</span>
          <span className="ins">선장 선언 — 손패 기준 (거짓 불가)</span>
          <span className="tmr">선장</span>
        </div>
        <div className="ab">
          <div className="ainfo" style={{ marginBottom: 9 }}>
            <span>
              장비만으로 극복 가능 →{" "}
              <b>
                {"'"}가능{"'"} 강제
              </b>{" "}
              · 터보 포함해야 가능 → <b>선택</b> · 터보 포함해도 불가 →{" "}
              <b>
                {"'"}불가{"'"} 강제
              </b>
              .
            </span>
          </div>
          <div className="acts">
            <button
              className="uact warn def"
              data-testid="celestia-declare-cannot-btn"
              type="button"
              onClick={() => actions.declareResolve(false)}
            >
              해결 불가
            </button>
            <button
              className="uact go"
              data-testid="celestia-declare-can-btn"
              type="button"
              onClick={() => actions.declareResolve(true)}
            >
              해결 가능
            </button>
          </div>
        </div>
      </div>

      {/* am-6 — reaction window (has cards) */}
      <div className={modeId === "am-6" ? "amode on" : "amode"} id="am-6">
        <div className="ah">
          <span className="pn">P6</span>
          <span className="ins">
            반응 — 카드 사용 또는 (액션 없음) → 장비 지불(P7) · 망원경은 7단계
          </span>
          <span className="tmr">내 차례</span>
        </div>
        <div className="ab">
          <div className="rxrow">
            {/* Jetpack */}
            <div className="rxopt">
              <div
                className={
                  !handHasReaction(hand, "jetpack") ? "rxcard off" : "rxcard"
                }
              >
                <svg className="ico" aria-hidden="true">
                  <use href="#ic-jetpack" />
                </svg>
                <span>제트팩</span>
              </div>
              <button
                className="rxbtn"
                data-testid="celestia-jetpack-btn"
                type="button"
                disabled={!handHasReaction(hand, "jetpack")}
                title={
                  !handHasReaction(hand, "jetpack") ? "카드 없음" : undefined
                }
                onClick={() => actions.reaction("jetpack")}
              >
                {handHasReaction(hand, "jetpack") ? "사용" : "없음"}
              </button>
            </div>
            {/* Alt route */}
            <div className="rxopt">
              <div
                className={
                  !handHasReaction(hand, "altRoute") ? "rxcard off" : "rxcard"
                }
              >
                <svg className="ico" aria-hidden="true">
                  <use href="#ic-altroute" />
                </svg>
                <span>샛길</span>
              </div>
              <button
                className="rxbtn"
                data-testid="celestia-altroute-btn"
                type="button"
                disabled={!handHasReaction(hand, "altRoute")}
                title={
                  !handHasReaction(hand, "altRoute") ? "카드 없음" : undefined
                }
                onClick={() => actions.reaction("altRoute")}
              >
                {handHasReaction(hand, "altRoute") ? "사용" : "없음"}
              </button>
            </div>
            {/* Wind gust */}
            <div className="rxopt">
              <div
                className={
                  !handHasReaction(hand, "windGust") ? "rxcard off" : "rxcard"
                }
              >
                <svg className="ico" aria-hidden="true">
                  <use href="#ic-windgust" />
                </svg>
                <span>돌풍</span>
              </div>
              <button
                className="rxbtn"
                data-testid="celestia-windgust-btn"
                type="button"
                disabled={!handHasReaction(hand, "windGust")}
                title={
                  !handHasReaction(hand, "windGust") ? "카드 없음" : undefined
                }
                onClick={() => actions.reaction("windGust")}
              >
                {handHasReaction(hand, "windGust") ? "사용" : "없음"}
              </button>
            </div>
            {/* Pass */}
            <button
              className="rxnone sel"
              data-testid="celestia-pass-btn"
              type="button"
              title="카드가 없거나 사용 안 함 · timeout 시 자동 선택"
              onClick={actions.pass}
            >
              (액션
              <br />
              없음)
            </button>
          </div>
        </div>
      </div>

      {/* am-6n — reaction window (no reaction cards) */}
      <div className={modeId === "am-6n" ? "amode on" : "amode"} id="am-6n">
        <div className="ah">
          <span className="pn">P6</span>
          <span className="ins">반응 — 사용할 대응 카드 없음</span>
          <span className="tmr">내 차례</span>
        </div>
        <div className="ab">
          <div className="ainfo">
            <span>
              손에 사용 가능한 대응 카드가 없습니다 — (액션 없음)으로 진행(장비
              지불 P7).
            </span>
          </div>
          <div className="acts" style={{ marginTop: 9 }}>
            <button
              className="uact pass def"
              data-testid="celestia-pass-btn"
              type="button"
              onClick={actions.pass}
            >
              (액션 없음) → 장비 지불(P7)
            </button>
          </div>
        </div>
      </div>

      {/* am-7 — resolution / equipment pay */}
      <div className={modeId === "am-7" ? "amode on" : "amode"} id="am-7">
        <div className="ah">
          <span className="pn">P7</span>
          <span className="ins">장비 지불 — 위기 ↕ 대응 카드</span>
          <span className="tmr">선장</span>
        </div>
        <div className="ab">
          <div className="pay">
            {symModes.map(({ sym, mode }) => {
              const eq = SYMBOL_EQUIP[sym];
              if (!eq) return null;

              if (mode === "turbo") {
                return (
                  <div
                    key={sym}
                    className={
                      turboOn ? "paycol turbo ok" : "paycol turbo fail"
                    }
                  >
                    <span
                      className={
                        sym === "lightning" ? "mini-die dark" : "mini-die"
                      }
                      style={
                        {
                          "--db": eq.colorVar,
                        } as React.CSSProperties
                      }
                    >
                      <svg className="ico" aria-hidden="true">
                        <use href={`#${eq.icon}`} />
                      </svg>
                    </span>
                    <span className="payarrow">↓</span>
                    <button
                      className={turboOn ? "payturbo" : "payturbo off"}
                      type="button"
                      aria-pressed={turboOn}
                      title="번개는 장비 없음 — 터보로만 극복 가능 (선택)"
                      onClick={() => setTurboOn((v) => !v)}
                    >
                      <svg className="ico" aria-hidden="true">
                        <use href="#ic-turbo" />
                      </svg>
                      <span className="lbl">
                        {turboOn ? "터보 사용" : "터보 미사용"}
                      </span>
                    </button>
                    <span className="ck">{turboOn ? "✓" : "✗"}</span>
                  </div>
                );
              }

              if (mode === "wreck") {
                return (
                  <div key={sym} className="paycol fail">
                    <span
                      className="mini-die"
                      style={
                        {
                          "--db": eq.colorVar,
                        } as React.CSSProperties
                      }
                    >
                      <svg className="ico" aria-hidden="true">
                        <use href={`#${eq.icon}`} />
                      </svg>
                    </span>
                    <span className="payarrow">↓</span>
                    <span className="paycard nocard">
                      대응
                      <br />
                      없음
                    </span>
                    <span className="ck">✗</span>
                  </div>
                );
              }

              // locked (forced)
              return (
                <div key={sym} className="paycol locked ok">
                  <span
                    className="mini-die"
                    style={
                      {
                        "--db": eq.colorVar,
                      } as React.CSSProperties
                    }
                  >
                    <svg className="ico" aria-hidden="true">
                      <use href={`#${eq.icon}`} />
                    </svg>
                  </span>
                  <span className="payarrow">↓</span>
                  <span
                    className="paycard"
                    style={
                      {
                        "--hz": eq.colorVar,
                      } as React.CSSProperties
                    }
                  >
                    <svg className="ico" aria-hidden="true">
                      <use href={`#${eq.icon}`} />
                    </svg>
                    {eq.cardLabel}
                  </span>
                  <span className="lk">
                    <span className="lock" />
                    강제
                  </span>
                </div>
              );
            })}
          </div>

          <div className={`pay-msg${p7MsgClass ? ` ${p7MsgClass}` : ""}`}>
            {p7Msg}
          </div>

          <div className="acts">
            {hasSpyglass && (
              <button
                className="uact gray"
                data-testid="celestia-spyglass-btn"
                type="button"
                onClick={actions.useSpyglass}
              >
                <svg className="ico" aria-hidden="true">
                  <use href="#ic-spyglass" />
                </svg>
                마법 망원경
              </button>
            )}
            {isCaptain && p7CanCrash && (
              <button
                className="uact warn def"
                data-testid="celestia-pass-crash-btn"
                type="button"
                onClick={actions.pass}
              >
                난파 수용
              </button>
            )}
            {isCaptain && p7CanGo && (
              <button
                className="uact go def"
                data-testid="celestia-pay-equipment-btn"
                type="button"
                onClick={() => actions.payEquipment(payCardIds)}
              >
                통과 완료
              </button>
            )}
            {/* Captain fallback: if neither go nor crash classified, still
                offer the wreck-accept path. Non-captain spyglass holders only
                ever see the spyglass button above. */}
            {isCaptain && !p7CanGo && !p7CanCrash && (
              <button
                className="uact warn def"
                data-testid="celestia-pass-crash-btn"
                type="button"
                onClick={actions.pass}
              >
                난파 수용
              </button>
            )}
          </div>
        </div>
      </div>

      {/* am-8 — treasure choice */}
      <div className={modeId === "am-8" ? "amode on" : "amode"} id="am-8">
        <div className="ah">
          <span className="pn">P8</span>
          <span className="ins">
            보물 — 선장부터 시계방향 (잔류 승객 + 선장)
          </span>
          <span className="tmr">내 차례</span>
        </div>
        <div className="ab">
          <div className="acts">
            <button
              className="uact primary def"
              data-testid="celestia-take-treasure-btn"
              type="button"
              onClick={() => actions.takeTreasure(treasureCityId)}
            >
              도시 {treasureCityId}+ 보물 가져오기
            </button>
          </div>
        </div>
      </div>

      {/* am-9 — end of leg (success) */}
      <div className={modeId === "am-9" ? "amode on" : "amode"} id="am-9">
        <div className="ah">
          <span className="pn">P9</span>
          <span className="ins">leg 성공 — 도시 이동 · 선장 교대</span>
          <span className="tmr">시스템</span>
        </div>
        <div className="ab">
          <div className="sysphase">
            <div className="sysproc">
              <div className="row">
                <span className="bigsp" aria-hidden="true" />
                <span>leg 결과 처리 중…</span>
              </div>
              <div className="sysbar">
                <i />
              </div>
            </div>
            <div className="sysres">
              <span className="hd ok">이번 leg 성공</span>
              <span>
                비행선이 다음 도시로 이동 · 잔류 승객 보물 확보 · 선장이 다음
                좌석으로 교대.
              </span>
              <span className="nx">
                <span className="dot" />
                잠시 후 다음 단계로 진행됩니다…
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* am-10 — end of journey */}
      <div className={modeId === "am-10" ? "amode on" : "amode"} id="am-10">
        <div className="ah">
          <span className="pn">P10</span>
          <span className="ins">새 모험 시작</span>
          <span className="tmr">시스템</span>
        </div>
        <div className="ab">
          <div className="sysphase">
            <div className="sysproc">
              <div className="row">
                <span className="bigsp" aria-hidden="true" />
                <span>새 모험 준비 중…</span>
              </div>
              <div className="sysbar">
                <i />
              </div>
            </div>
            <div className="sysres">
              <span className="hd ok">새 모험 시작</span>
              <span>
                전원 장비 카드 +1 · 여행자 말 비행선 복귀 · 도시 1로 복귀 · 선장
                교대. (≥50점이면 게임 종료 판정)
              </span>
              <span className="nx">
                <span className="dot" />
                잠시 후 다음 단계로 진행됩니다…
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Need React for JSX CSSProperties
import type React from "react";
